/**
 * Office-notification email-activity tracking (GOTU-4002 / GOTU-3719).
 *
 * Offices enter the portal via "View in Web Portal" CTAs in transactional
 * emails. We fire a `dental_portal:office_notification:*` event on landing so
 * PostHog can measure which notification types drive engagement.
 *
 * The link contract is the UTM convention resolved in GOTU-4135 (the BE/email
 * ticket), read by {@link parseNotificationParams}: `utm_source=email`,
 * `utm_campaign=<notification key>`, `shift_ids=<csv>`, `professional_id=<id>`.
 */

/** Where the office entered the portal from. Only `email` is tracked today. */
export type TNotificationOrigin = 'email';

/**
 * The notification (email) types we track, carried on the email CTA as the
 * `utm_campaign` value and used as the suffix of the fired PostHog event name.
 */
export const OFFICE_NOTIFICATION_KEYS = [
  'candidate_applied',
  'candidate_applied_counter',
  'offer_expiration_warning',
  'end_of_day_review',
] as const;

export type TOfficeNotificationKey = (typeof OFFICE_NOTIFICATION_KEYS)[number];

/**
 * Engagement timing shared by every office-notification event. Keys are already
 * snake_cased so an event payload can spread this directly.
 *
 * Hour-of-day / day-of-week were dropped per GOTU-4135 — PostHog's native event
 * `$timestamp` already covers "when was the link clicked", so we don't duplicate
 * it in the payload.
 */
export interface IEngagementContext {
  /** Whole minutes from landing to shift start. Omitted when no shift is in context. */
  time_to_shift_start_minutes?: number;
}
